/**
 * @fileoverview Module for `src/admin/BatchApprove.js`.
 */

import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  TextInput,
  Filter,
  FunctionField,
  Button,
  useRefresh,
} from 'react-admin';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { makeStyles } from '@material-ui/core/styles';

/**
 * Shared table sizing styles for batch approval views.
 */
const useStyles = makeStyles({
  letterSample: {
    minWidth: '15rem',
    padding: '2px',
  },
  colSM: {
    maxWidth: '5rem',
    wordWrap: 'break-word',
    padding: '2px',
  },
  col: {
    wordWrap: 'break-word',
    padding: '2px',
  },
});

/**
 * Bulk action button for approving selected pre-qualified users.
 *
 * @param {{selectedIds: string[]}} params
 * @returns {JSX.Element} Batch approve action button.
 */
const UserTableBulkActionButtons = ({ selectedIds }:any) => {
  const refresh = useRefresh();
  const auth = useAuth();
  /**
   * Sends the selected user IDs to the batch-approve API endpoint and refreshes the list.
   */
  const handleBatchApprovePending = async (event) => {
    event.preventDefault();
    const accessToken = await auth.getAccessToken();
    await axios({
      method: 'POST',
      headers: {
        Authorization: 'Bearer '.concat(accessToken),
      },
      url: `${ process.env.REACT_APP_API_URL }/v1/users/batchApprove`,
      data: {
        ids: selectedIds,
      },
    });
    refresh();
  };

  return (
    <Button label='Approve Selected Users' onClick={ handleBatchApprovePending } />
  );
};


/**
 * Social profile icon links for each user row.
 *
 * @returns {JSX.Element} Social links field.
 */
const SocialLink = () => {
  const classes = useStyles();
  return (
    <FunctionField
      cellClassName={ classes.colSM }
      headerClassName={ classes.colSM }
      label='Social Links'
      render={ (record) => (
        <React.Fragment>
          { record.twitterProfileUrl && (
            <a href={ record.twitterProfileUrl }>
              <i className='fa fa-twitter' />
            </a>
          ) }
          { record.facebookProfileUrl && (
            <a href={ record.facebookProfileUrl }>
              <i className='fa fa-facebook-f' />
            </a>
          ) }
          { record.linkedinProfileUrl && (
            <a href={ record.linkedinProfileUrl }>
              <i className='fa fa-linkedin' />
            </a>
          ) }
        </React.Fragment>
      ) }
    />
  );
};

/**
 * Admin link field to open the user detail page.
 *
 * @returns {JSX.Element} Auth0 id link field.
 */
const BatchApproveLink = () => {
  const classes = useStyles();
  return (
    <FunctionField
      cellClassName={ classes.colSM }
      headerClassName={ classes.colSM }
      label='Auth0Id'
      render={ (record) => (
        <Link target='_blank' to={ `/user/${ record.auth0Id }` }>
          { record.auth0Id }
        </Link>
      ) }
      sortable={ false }
    />
  );
};

/**
 * Filter controls for batch approval queue queries.
 *
 * @param {Object} props
 * @returns {JSX.Element} Batch approval filters.
 */
const BatchApproveFilters = (props) => (
  <Filter { ...props }>
    <TextInput alwaysOn label='Full Name' source='fullName:contains' />
    <TextInput alwaysOn label='Auth0Id' source='auth0Id:startsWith' />
    <TextInput alwaysOn label='Email' source='email:startsWith' />
    <DateField alwaysOn label='Signup date' showTime source='createdAt' />
    <TextInput label='Partners' source='partners:startsWith' />
    <TextInput label='Zip' source='zip' />
  </Filter>
);


/**
 * List view for selecting and approving pre-qualified users in bulk.
 *
 * @param {Object} props
 * @returns {JSX.Element} Batch approval table.
 */
const BatchApprove = (props) => {
  const classes = useStyles();

  return (
    <div>
      <List
        basePath='/users'
        bulkActionButtons={ <UserTableBulkActionButtons { ...props } /> }
        filterDefaultValues={{
          qualState: 'pre_qualified',
          'letterWritingSample:isNotNull': null,
          'pledged_vote_at:isNotNull': null,
          'accepted_code_at:isNotNull': null,
          'full_name:isNotNull': null,
          'preferred_name:isNotNull': null,
          'confirmed_understood_instructions_at:isNotNull': null,
          'zip:isNotNull': null,
        }}
        filters={ <BatchApproveFilters /> }
        resource='Users'
        sort={{ field: 'createdAt', order: 'ASC' }}
        title='Batch Approve'
      >
        <Datagrid
          optimized
        >
          <TextField cellClassName={ classes.col } headerClassName={ classes.col } source='fullName' />
          { BatchApproveLink() }
          <TextField cellClassName={ classes.colSM } headerClassName={ classes.colSM } source='email' />
          { SocialLink() }
          <DateField cellClassName={ classes.colSM } headerClassName={ classes.colSM } showTime source='createdAt' />
          <TextField cellClassName={ classes.letterSample } headerClassName={ classes.letterSample } label='Letter Writing Sample' sortable={ false } source='letterWritingSample' />
          <TextField cellClassName={ classes.colSM } headerClassName={ classes.colSM } source='partners' />
          <TextField cellClassName={ classes.colSM } headerClassName={ classes.colSM } source='zip' />
        </Datagrid>
      </List>
    </div>
  );
};
export default BatchApprove;
